// This is a wrapper around MaterialReactTable that disables memoization 
// until MRT is compatible with React Compiler

import FormControl from "@mui/material/FormControl";
import { visuallyHidden } from "@mui/utils";
import { alpha, styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { BoxProps } from "@mui/material/Box";
import type { Theme } from "@mui/material/styles";
import type { PaperProps } from "@mui/material/Paper";
import type { TableProps } from "@mui/material/Table";
import type { TableContainerProps } from "@mui/material/TableContainer";
import type { TableCellProps } from "@mui/material/TableCell";
import type { TextFieldProps } from "@mui/material/TextField";
import {
  getAllLeafColumnDefs,
  getColumnId,
  getDefaultColumnOrderIds,
  MaterialReactTable,
  type MRT_Cell,
  type MRT_Column,
  type MRT_Row,
  type MRT_RowData,
  type MRT_StatefulTableOptions,
  type MRT_TableInstance,
  type MRT_TableOptions,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { RowActionsColumnHeader } from "_shared/_views/tables/row-actions";
import { TableCards, type TableCardsOptions } from "_shared/_views/tables/table-cards";
import { ReaderColumnChoiceContext } from "_shared/_views/tables/table-column-preferences-context";
import { usePersistedColumnVisibility } from "_shared/_views/tables/table-column-visibility-preference";
import {
  type PersistedColumnLayout,
  usePersistedColumnLayout,
  useSaveColumnLayout,
} from "_shared/_views/tables/table-column-layout-preference";
import { TableSelectCheckbox } from "_shared/_views/tables/table-select-checkbox";
import { TableScrollButtons } from "_shared/_views/tables/table-scroll-buttons";
import { useTableCellTooltip } from "_shared/_views/tables/table-cell-tooltip";
import { useReportDrawnRows, useTurnToRevealedRow } from "_shared/_views/tables/table-reveal-row";
import { headerToolsOnHoverSx } from "_shared/_views/tables/table-header-tools-on-hover";
import { LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from "_shared/_views/tables/table-page-size";
import { useResizableColumns } from "_shared/_views/tables/table-column-widths";
import { FoldedColumnsContext, foldedColumnsSx, withFoldHosts } from "_shared/_views/tables/folded-columns";
import {
  fittedSizingOptions,
  hasColumnKinds,
  useFitColumnsToPane,
  useFittedColumnsState,
  type FittedColumns,
} from "_shared/_views/tables/use-fitted-columns";

const legacyTextFieldRootProps = new Set([
  "as",
  "FormHelperTextProps",
  "InputLabelProps",
  "InputProps",
  "ownerState",
  "SelectProps",
  "sx",
  "theme",
  "inputProps",
]);

export const MrtTextFieldRoot = styled(FormControl, {
  shouldForwardProp: (prop) => !legacyTextFieldRootProps.has(prop.toString()),
})({});

function withMrtTextFieldRootSlot<TProps extends TextFieldProps>(textFieldProps: TProps | undefined): TProps {
  return {
    ...textFieldProps,
    slots: {
      ...textFieldProps?.slots,
      root: textFieldProps?.slots?.root ?? MrtTextFieldRoot,
    },
  } as TProps;
}

function parseFromValuesOrFunc<TArgs, TValue>(
  valueOrFunc: TValue | ((args: TArgs) => TValue) | undefined,
  args: TArgs,
): TValue | undefined {
  return typeof valueOrFunc === "function" ? (valueOrFunc as (args: TArgs) => TValue)(args) : valueOrFunc;
}

const HiddenHeaderLabel = styled("span")({ ...visuallyHidden });

/** The row-select column's header where there is no select-all to draw. See its use below. */
function RowSelectColumnHeader() {
  return <HiddenHeaderLabel>Select</HiddenHeaderLabel>;
}

type MRTNoMemoProps<TData extends MRT_RowData> = {
  options?: MRT_TableOptions<TData>;
  /**
   * Draws a round button over each edge of the table while it can scroll that way — see
   * `TableScrollButtons` and the note above `visibleScrollbarStyles`. Opt-in: the fee schedule grids
   * take it, where a narrow pane scrolls a table whose columns all matter (#5395).
   */
  scrollButtons?: boolean;
  /**
   * Remembers, in this browser, which columns the reader hides through Show/Hide columns, under this
   * key — one per table, so hiding a column on one table leaves every other alone (#2788). Ignored by
   * a table that sets `state.columnVisibility` or `onColumnVisibilityChange` itself: that table owns
   * the choice, and calls `usePersistedColumnVisibility` directly where it keeps it.
   */
  persistColumnVisibilityAs?: string;
  /**
   * The table's key on the reader's account, under which the widths they drag and the order they put
   * the columns in are kept in its current view (#6714). Defaults to `persistColumnVisibilityAs`; a
   * table that keeps its Show/Hide columns choice itself names its key here. Ignored for a part the
   * table controls itself: its `state.columnSizing` or `onColumnSizingChange`, its `state.columnOrder`
   * or `onColumnOrderChange`. The order is kept only for a table that lets the reader reorder.
   */
  tableKey?: string;
  /**
   * The reader's own Show/Hide columns choice, for a table that keeps it itself through
   * `usePersistedColumnVisibility` — so its Show/Hide columns button can show that columns are put
   * away. A table that persists through `persistColumnVisibilityAs` needs nothing here.
   */
  columnChoice?: Readonly<Record<string, boolean>>;
  /**
   * The id of a row to turn the table's page to, each time it names a new one — the row a traverser
   * has just landed on. See `useTurnToRevealedRow`.
   */
  revealRowId?: string;
  /** Hears the rows the table draws, in its sorted and filtered order. See `useReportDrawnRows`. */
  onDrawnRowsChange?: (rows: readonly TData[] | undefined) => void;
  /**
   * Draws the rows as cards under 600px, with Sort and Filter sheets (#6715). For an item list whose
   * columns have kinds; see `TableCards`. A comparison table, read across its columns, leaves it off
   * and keeps scrolling: fee categories, the permissions matrix, staffing, a team roster.
   *
   * Options in place of `true` say more about the cards (#7163): `ticks` for a table whose rows are
   * ticks rather than things to open, such as a template picker, and `sortInToolbar` for a table that
   * draws `TableSortButton` in its own toolbar. See `TableCardsOptions`.
   */
  cards?: boolean | TableCardsOptions;
};

type MrtBodyCellArgs<TData extends MRT_RowData> = {
  cell: MRT_Cell<TData>;
  column: MRT_Column<TData>;
  row: MRT_Row<TData>;
  table: MRT_TableInstance<TData>;
};
type MrtSelectCellArgs<TData extends MRT_RowData> = {
  row: MRT_Row<TData>;
  staticRowIndex?: number;
  table: MRT_TableInstance<TData>;
};

/**
 * Keeps the actions column to the width of its controls, and centres them in it.
 *
 * Two layouts need two answers. A `grid` table sizes its columns from MRT's own `size`/`grow`, so
 * the tables using it already pin their own width. A `semantic` one — the default — is a real
 * `<table>` under `table-layout: auto`, where MRT's sizing options do nothing and the browser hands
 * the column a share of whatever is left over: measured before this, 280px of column around 120px of
 * buttons on the fee schedules page, and 152px around a single 32px menu on marketplace profiles.
 * A tiny declared width is the standard way to tell the auto algorithm to shrink a column to its
 * content and give the slack to the columns that hold the data.
 *
 * `align` alone does not centre the controls: the row's container is a block-level flex box, so it
 * fills the cell and packs its buttons at the start regardless. The children have to be told.
 *
 * Column-level cell props *replace* the table's own for that column rather than adding to them, so
 * a table that paints its rows — the redline tables shade a resolved-and-accepted row green across
 * every cell — would lose that paint on this one column. The table's props are read here and kept
 * underneath, with the width and centring layered on top.
 */
/** The actions column, sized to its controls and no wider. */
const ROW_ACTIONS_COLUMN_ID = "mrt-row-actions";
const oneCollapsedButton = 48;

/**
 * How the actions column is presented, layered onto whatever the table already says about its
 * cells rather than replacing it.
 *
 * Two layouts need two answers. A `grid` table sizes its columns from MRT's own `size`/`grow`, so
 * the tables using it already pin their own width. A `semantic` one — the default — is a real
 * `<table>` under `table-layout: auto`, where MRT's sizing options do nothing and the browser hands
 * the column a share of whatever is left over: measured before this, 280px of column around 120px of
 * buttons on the fee schedules page, and 152px around a single 32px menu on marketplace profiles.
 * A tiny declared width is the standard way to tell the auto algorithm to shrink a column to its
 * content and give the slack to the columns that hold the data.
 *
 * `align` alone does not centre the controls: the row's container is a block-level flex box, so it
 * fills the cell and packs its buttons at the start regardless. The children have to be told.
 */
function rowActionsCellSx(layoutMode: MRT_TableOptions<MRT_RowData>["layoutMode"]) {
  const shrinkToContent = layoutMode === "grid" ? {} : { width: "1%", whiteSpace: "nowrap" as const };
  return {
    px: 0.5,
    minWidth: oneCollapsedButton,
    ...shrinkToContent,
    "& > *": { justifyContent: "center" },
    // MRT's own menu button carries a 10px left margin, which pushes it off centre by five.
    "& > .MuiIconButton-root": { marginLeft: 0, marginRight: 0 },
  };
}

/**
 * Adds that presentation to the table's own body-cell props, for the actions column alone.
 *
 * This is deliberately not a column-level `muiTableBodyCellProps`: those *replace* the table's for
 * the column they name, and the tables that paint their rows — the redline tables shade a
 * resolved-and-accepted row green across every cell — would lose the paint on this one column.
 * Extending the table-level callback keeps every such rule and adds the width and centring after it.
 */
function withRowActionsCellProps<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  const tableCellProps = baseOptions.muiTableBodyCellProps;
  return (cellArgs: MrtBodyCellArgs<TData>): TableCellProps => {
    const inherited = (typeof tableCellProps === "function" ? tableCellProps(cellArgs) : tableCellProps) ?? {};
    if (cellArgs.column.id !== ROW_ACTIONS_COLUMN_ID) return inherited;
    // One object, not an array: MRT spreads `sx` into its own, and a spread array becomes
    // `{0: …, 1: …}` — which is how the actions cell silently lost every style the table gave it.
    const inheritedSx = typeof inherited.sx === "function" ? inherited.sx : (inherited.sx ?? {});
    return {
      ...inherited,
      align: "center" as const,
      sx: ((theme: Theme) => ({
        ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
        ...rowActionsCellSx(baseOptions.layoutMode),
      })) as TableCellProps["sx"],
    };
  };
}

/**
 * The same, for the actions column's header.
 *
 * A column-level `muiTableHeadCellProps` has exactly the replacing problem described above, and the
 * team roster is the case that shows it: that table paints the pinned actions header with the
 * theme's own paper so the column it floats over cannot read through it, and a column-level
 * override drops that paint back to `inherit` — "Role" renders as "R le" behind the icons (#4955).
 */
function withRowActionsHeadCellProps<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  const headCellProps = baseOptions.muiTableHeadCellProps;
  return (headArgs: { column: MRT_Column<TData>; table: MRT_TableInstance<TData> }): TableCellProps => {
    const inherited = parseFromValuesOrFunc(headCellProps, headArgs) ?? {};
    if (headArgs.column.id !== ROW_ACTIONS_COLUMN_ID) return inherited;
    const inheritedSx = inherited.sx ?? {};
    return {
      ...inherited,
      align: "center" as const,
      sx: ((theme: Theme) => ({
        ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
        ...rowActionsCellSx(baseOptions.layoutMode),
      })) as TableCellProps["sx"],
    };
  };
}

/**
 * Lets a table with no rows take the height of the pane it is drawn in, so the message that stands
 * in for the rows is centred in that pane rather than tucked under the header.
 *
 * This is only ever in play while the table is empty. It changes the height of the one row MRT
 * draws in place of the rows, and while there are rows there is nothing here to distort.
 *
 * The two layout modes need two answers, and both were measured. A `semantic` table is a real
 * `<table>`: a height on it goes to the body row, and `vertical-align: middle` — which a `<td>` has
 * of its own accord — centres the message in it, with the header row keeping its own 52px. A `grid`
 * table is a CSS grid whose tracks all stretch to share out the slack, and the header grew from
 * 52px to 181px doing exactly that, so its tracks are named instead: the head takes what it needs
 * and the body takes the rest.
 *
 * Where the pane has no height of its own to give — a table that is as tall as its content — none
 * of this resolves to anything and nothing moves. See
 * [table empty states](../../../../../docs/software-design/table-empty-states.md).
 */
function emptyTableFillsThePane<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  return {
    height: "100%",
    // The cell that holds the message is not a row and should not be dressed as one: MRT's own
    // padding pushes the message off centre by half of it, and the bottom border draws a rule
    // across the middle of an otherwise empty pane, which reads as a row that failed to render.
    "& > tbody > tr > td": { border: "none", padding: 0 },
    ...(baseOptions.layoutMode?.startsWith("grid")
      ? { gridTemplateRows: baseOptions.enableTableHead === false ? "1fr" : "auto 1fr" }
      : {}),
  };
}

/**
 * Adds that height to the table's own props, for the renders where the table has no rows.
 *
 * The creating row counts as a row: MRT draws it in the body, and stretching the body around a real
 * row is the one thing this is not for.
 */
function withEmptyTableProps<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  const tableProps = baseOptions.muiTableProps;
  return (tableArgs: { table: MRT_TableInstance<TData> }): TableProps => {
    const inherited = parseFromValuesOrFunc(tableProps, tableArgs) ?? {};
    const isEmpty = !tableArgs.table.getRowModel().rows.length && !tableArgs.table.getState().creatingRow;
    if (!isEmpty) return inherited;
    const inheritedSx = inherited.sx ?? {};
    return {
      ...inherited,
      sx: ((theme: Theme) => ({
        ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
        ...emptyTableFillsThePane(baseOptions),
      })) as TableProps["sx"],
    };
  };
}

/**
 * A scroll handle the reader can actually see, for a table whose columns run past its pane.
 *
 * Every one of these tables is drawn in a panel narrower than its columns, so the rows continue past
 * the right-hand edge — and until now the only thing that said so was a pair of chevrons this app drew
 * for itself, stacked under the paginator's own chevrons where the table had one (#4955). A real
 * scrollbar says it better and says it in the place a reader already looks.
 *
 * It has to be styled rather than left to the platform — see the rule below for which engine takes
 * which half of it, and why they cannot both be given.
 *
 * It appears when there IS overflow and not otherwise, because the container scrolls on `auto`: a
 * permanently drawn empty trough under a table that fits says there is somewhere to go when there is
 * not.
 *
 * A table can also ask for `scrollButtons`, and a clickable control comes back — on purpose, and in a
 * different form from the chevrons #4955 removed (#5395). Those sat in a strip under the rows, stacked
 * with the paginator's own chevrons. The new ones float over the table's own edges, centred on the
 * rows the reader is looking at; each appears only while there is more in its direction; each steps
 * about the width of the visible table; and neither covers the pinned actions column. They return because the scrollbar alone
 * did not carry the fee schedule grids: the fit that hid their columns in a narrow pane gave way to a
 * table that scrolls, and a thin bar under a tall table is not where a reader looks for the column
 * they cannot see. The scrollbar stays. The buttons are the part of the affordance a reader can click
 * without having to find it first.
 *
 * A capture cannot photograph one without help: Playwright passes `--hide-scrollbars` to every
 * headless Chromium of its own accord, so a spec meaning to show a scrolling surface has to drop it
 * through `launchOptions.ignoreDefaultArgs`.
 */
export function visibleScrollbarStyles(theme: Theme) {
  const trough = alpha(theme.palette.common.black, 0.06);
  const handle = alpha(theme.palette.common.black, 0.32);
  return {
    /**
     * The two families are mutually exclusive, and that is not a style preference.
     *
     * Chromium has ignored every `::-webkit-scrollbar-*` rule on an element that also carries
     * `scrollbar-width` or `scrollbar-color` since M121 — and the standard properties alone leave the
     * scrollbar an OVERLAY one, which Windows 11 and macOS both hide until it is already moving. The
     * two together therefore produce exactly the scrollbar this rule exists to replace. Styling the
     * pseudo-elements is what turns an overlay scrollbar into one that is laid out and always drawn
     * while the container overflows, so the engines that have them are given only those; Firefox,
     * which has neither the pseudo-elements nor the ignore rule, is given the standard properties.
     */
    "@supports not selector(::-webkit-scrollbar)": {
      scrollbarWidth: "thin" as const,
      scrollbarColor: `${handle} ${trough}`,
    },
    "&::-webkit-scrollbar": { WebkitAppearance: "none", height: 10, width: 10 },
    "&::-webkit-scrollbar-track": { backgroundColor: trough, borderRadius: 5 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: handle,
      backgroundClip: "content-box",
      border: "2px solid transparent",
      borderRadius: 5,
      "&:hover": { backgroundColor: alpha(theme.palette.common.black, 0.5) },
    },
    "&::-webkit-scrollbar-corner": { backgroundColor: "transparent" },
  };
}

/**
 * Adds that scrollbar to the table's own container props, underneath whatever the table already says
 * about its container — so a table that pins its own `overflowX`, height or position keeps all of it
 * and only gains the handle.
 */
function withVisibleScrollbar<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  const containerProps = baseOptions.muiTableContainerProps;
  return (containerArgs: { table: MRT_TableInstance<TData> }): TableContainerProps => {
    const inherited = parseFromValuesOrFunc(containerProps, containerArgs) ?? {};
    const inheritedSx = inherited.sx ?? {};
    return {
      ...inherited,
      sx: ((theme: Theme) => ({
        ...visibleScrollbarStyles(theme),
        ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
      })) as TableContainerProps["sx"],
    };
  };
}

/**
 * Gives the paper a positioning context, so the scroll buttons drawn into it are placed against it.
 * In full screen MRT makes the paper `position: fixed` inline, which is a positioning context too.
 */
function withPositionedPaper<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  const paperProps = baseOptions.muiTablePaperProps;
  return (paperArgs: { table: MRT_TableInstance<TData> }): PaperProps => {
    const inherited = parseFromValuesOrFunc(paperProps, paperArgs) ?? {};
    const inheritedSx = inherited.sx ?? {};
    return {
      ...inherited,
      sx: ((theme: Theme) => ({
        position: "relative",
        ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
      })) as PaperProps["sx"],
    };
  };
}

/**
 * A phone's pager: the same controls in a strip half the height.
 *
 * MRT stands a 32px pager in a 67–74px toolbar — a 3.5rem floor, 8px round the row and 12px above and
 * below the pager itself — and on a phone that is a fifth of the room a proposal tab has for its rows
 * (#5907). The rows-per-page picker goes too: a phone reader pages, they do not re-size the page.
 */
function withPhoneSizedPager<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>) {
  const toolbarProps = baseOptions.muiBottomToolbarProps;
  const paginationProps = baseOptions.muiPaginationProps;
  return {
    muiBottomToolbarProps: (toolbarArgs: { table: MRT_TableInstance<TData> }): BoxProps => {
      const inherited = parseFromValuesOrFunc(toolbarProps, toolbarArgs) ?? {};
      const inheritedSx = inherited.sx ?? {};
      return {
        ...inherited,
        sx: ((theme: Theme) => ({
          ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
          minHeight: 0,
          "& > .MuiBox-root": { padding: 0 },
          "& .MuiTablePagination-root": { paddingBlock: theme.spacing(0.5) },
        })) as BoxProps["sx"],
      };
    },
    muiPaginationProps: (paginationArgs: { table: MRT_TableInstance<TData> }) => ({
      ...parseFromValuesOrFunc(paginationProps, paginationArgs),
      showRowsPerPage: false,
    }),
  };
}

/**
 * 25 rows a page, with 10, 25, 50 and 100 to choose from, on every table (`table-page-size.ts`, #6713).
 * Lists opened on 5, 10, 25 or 100 rows and offered MRT's eight sizes. A table that states its own
 * page size or sizes keeps them.
 */
function withListPageSizes<TData extends MRT_RowData>(baseOptions: MRT_TableOptions<TData>): MRT_TableOptions<TData> {
  const paginationProps = baseOptions.muiPaginationProps;
  return {
    ...baseOptions,
    initialState: { pagination: { pageIndex: 0, pageSize: LIST_PAGE_SIZE }, ...baseOptions.initialState },
    muiPaginationProps: (paginationArgs: { table: MRT_TableInstance<TData> }) => ({
      rowsPerPageOptions: [...LIST_PAGE_SIZE_OPTIONS],
      ...parseFromValuesOrFunc(paginationProps, paginationArgs),
    }),
  };
}

/** The columns, each with the fit's minimum as its `minSize` wherever the fit has measured one. */
function withFittedMinimums<TData extends MRT_RowData>(
  columns: MRT_TableOptions<TData>["columns"],
  minimums: Readonly<Record<string, number>>,
): MRT_TableOptions<TData>["columns"] {
  return columns.map((column) => {
    const id = column.id ?? column.accessorKey;
    const minimum = id === undefined ? undefined : minimums[id];
    const nested = column.columns ? { columns: withFittedMinimums(column.columns, minimums) } : {};
    if (minimum === undefined || column.meta?.kind === undefined) return { ...column, ...nested };
    return { ...column, ...nested, minSize: minimum };
  });
}

const noFoldedColumns: Readonly<Record<string, string>> = {};

type ResizableColumnCellProps<TData extends MRT_RowData> = ReturnType<typeof useResizableColumns<TData>>;

/**
 * What every list table is, so no list page has to say it (#6708).
 *
 * A table opts in by giving its columns a kind (`meta.kind`, see `table-column-fit.ts`). It then gets:
 *
 * - a grid layout and compact density;
 * - its columns fitted to its pane, a heading never clipped or wrapped;
 * - resizing that follows the pointer, where a dragged width changes that column alone and a
 *   double-click on its heading returns it to its fitted width (`useResizableColumns`);
 * - its column menus, and the arrows of columns it is not sorted by, only on hover (`headerToolsOnHoverSx`);
 * - one container width whether or not a vertical scrollbar shows, so the fit settles on its first pass
 *   rather than chasing the scrollbar it retires (#5121);
 * - short of room, its lowest-priority columns folded onto their hosts' second lines before it scrolls
 *   (`meta.fold`, see `folded-columns.tsx`, #6711).
 *
 * Columns do not grow on their own: the fit hands out the pane's whole width itself, and a column that
 * grew would take room a drag gave back to the others. Each column's `minSize` is the fit's minimum
 * once it is measured, so a width the column declared for its first paint cannot hold it wider than
 * the fit gave it, and a drag stops at its heading. Anything else the table states itself still wins.
 */
function withListTableDefaults<TData extends MRT_RowData>(
  pageOptions: MRT_TableOptions<TData>,
  columns: MRT_TableOptions<TData>["columns"],
  fit: FittedColumns,
  widths: ResizableColumnCellProps<TData>,
): MRT_TableOptions<TData> {
  const sizing = fittedSizingOptions(fit, pageOptions);
  const headCellProps = pageOptions.muiTableHeadCellProps;
  const bodyCellProps = pageOptions.muiTableBodyCellProps;
  const containerProps = pageOptions.muiTableContainerProps;
  const tableProps = pageOptions.muiTableProps;
  // The actions column's width comes from its kind, measured from the controls its rows draw, and not
  // from a number each page worked out for itself (48, 88, a sum of buttons) (#6711).
  const { size: _size, minSize: _minSize, maxSize: _maxSize, grow: _grow, ...actionsColumn } =
    pageOptions.displayColumnDefOptions?.["mrt-row-actions"] ?? {};
  return {
    layoutMode: "grid",
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    ...pageOptions,
    columns,
    defaultColumn: { grow: false, ...pageOptions.defaultColumn },
    displayColumnDefOptions: {
      ...pageOptions.displayColumnDefOptions,
      "mrt-row-actions": { ...actionsColumn, meta: { kind: "actions", ...actionsColumn.meta } },
    },
    initialState: { density: "compact", ...pageOptions.initialState },
    state: { ...pageOptions.state, columnSizing: sizing.columnSizing },
    onColumnSizingChange: sizing.onColumnSizingChange,
    muiTableHeadCellProps: (headArgs) => {
      const inherited = parseFromValuesOrFunc(headCellProps, headArgs) ?? {};
      const inheritedSx = inherited.sx ?? {};
      return {
        ...widths.headCellProps(headArgs.column.id, headArgs.table),
        ...inherited,
        sx: ((theme: Theme) => ({
          ...headerToolsOnHoverSx(headArgs.table.options.mrtTheme.baseBackgroundColor),
          // MRT lets a heading of 20 characters or more wrap. The fit keeps every column at least as wide
          // as its heading, so none has to.
          "& .Mui-TableHeadCell-Content-Wrapper": { whiteSpace: "nowrap" },
          ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
        })) as TableCellProps["sx"],
      };
    },
    muiTableBodyCellProps: (cellArgs) => ({
      ...widths.bodyCellProps(cellArgs.column.id),
      ...parseFromValuesOrFunc(bodyCellProps, cellArgs),
    }),
    muiTableContainerProps: (containerArgs) => {
      const inherited = parseFromValuesOrFunc(containerProps, containerArgs) ?? {};
      const inheritedSx = inherited.sx ?? {};
      return {
        ...inherited,
        sx: ((theme: Theme) => ({
          scrollbarGutter: "stable",
          ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
        })) as TableContainerProps["sx"],
      };
    },
    // A folded column reads under its host, so its own cells leave the row (#6711).
    muiTableProps: (tableArgs) => {
      const inherited = parseFromValuesOrFunc(tableProps, tableArgs) ?? {};
      const inheritedSx = inherited.sx ?? {};
      return {
        ...inherited,
        sx: ((theme: Theme) => ({
          ...(typeof inheritedSx === "function" ? inheritedSx(theme) : inheritedSx),
          ...foldedColumnsSx(fit.foldedInto),
        })) as TableProps["sx"],
      };
    },
  };
}

/** The ids of a table's own columns, the columns its groups hold included. */
function leafColumnIds<TData extends MRT_RowData>(columns: MRT_TableOptions<TData>["columns"] | undefined): ReadonlySet<string> {
  return new Set(getAllLeafColumnDefs(columns ?? []).map((column) => getColumnId(column)));
}

/** A table that is not a list table, with the reader's dragged widths kept as MRT's sizing state. */
function withReaderColumnSizing<TData extends MRT_RowData>(
  options: MRT_TableOptions<TData>,
  layout: PersistedColumnLayout,
): MRT_TableOptions<TData> {
  return { ...options, state: { ...options.state, columnSizing: layout.sizing }, onColumnSizingChange: layout.setSizing };
}

/**
 * A table with the reader's column order. MRT is handed its whole order, its own display columns in their
 * places, worked out as MRT works out its first one: the stored order first, then every column it leaves
 * out, in the table's own order. So a column added since the reader chose sits at the end.
 */
function withReaderColumnOrder<TData extends MRT_RowData>(
  options: MRT_TableOptions<TData>,
  layout: PersistedColumnLayout,
): MRT_TableOptions<TData> {
  const startingOrder = layout.order.length > 0 ? layout.order : (options.initialState?.columnOrder ?? []);
  const columnOrder = layout.orderIsWhole
    ? [...layout.order]
    : getDefaultColumnOrderIds({
        // MRT's own defaults, which decide where its display columns go.
        positionActionsColumn: "first",
        positionExpandColumn: "first",
        ...options,
        layoutMode: options.layoutMode ?? (options.enableColumnResizing ? "grid-no-grow" : "semantic"),
        state: { ...options.initialState, ...options.state, columnOrder: [...startingOrder] },
      } as MRT_StatefulTableOptions<TData>);
  return {
    ...options,
    state: { ...options.state, columnOrder },
    onColumnOrderChange: (updater) => layout.setOrder(updater, columnOrder),
  };
}

export function MRTNoMemo<TData extends MRT_RowData>(props: Readonly<MRTNoMemoProps<TData>>) {
  "use no memo";

  const theme = useTheme();
  const phoneQuery = theme.breakpoints.down("sm");
  const isPhone = useMediaQuery(phoneQuery);
  const pageOptions = props.options ?? ({} as MRT_TableOptions<TData>);
  const isListTable = hasColumnKinds(pageOptions.columns);
  const layoutKey = props.tableKey ?? props.persistColumnVisibilityAs;
  const columnIds = useMemo(() => leafColumnIds(pageOptions.columns), [pageOptions.columns]);
  const layout = usePersistedColumnLayout(layoutKey, columnIds);
  const persistsSizing =
    Boolean(layoutKey) && pageOptions.state?.columnSizing === undefined && pageOptions.onColumnSizingChange === undefined;
  const fitState = useFittedColumnsState();
  // A list table's dragged widths are the reader's widths over its fitted ones, so those are what is kept.
  const fit = persistsSizing ? { ...fitState, reader: layout.sizing, setReader: layout.setSizing } : fitState;
  const widths = useResizableColumns<TData>(undefined, (columnId) => columnId in fit.reader);
  const foldHostColumns = useMemo(
    () => (isListTable ? withFoldHosts(pageOptions.columns) : pageOptions.columns),
    [isListTable, pageOptions.columns],
  );
  const fittedColumns = useMemo(
    () => (isListTable ? withFittedMinimums(foldHostColumns, fit.minimums) : foldHostColumns),
    [fit.minimums, foldHostColumns, isListTable],
  );
  const listOptions = isListTable ? withListTableDefaults(pageOptions, fittedColumns, fit, widths) : pageOptions;
  const baseOptions = withListPageSizes(
    persistsSizing && !isListTable ? withReaderColumnSizing(listOptions, layout) : listOptions,
  );
  const cellTooltip = useTableCellTooltip();
  // An empty table has no row to act on, and its actions column was still drawn — a blank pinned
  // strip over the last header, cutting it to "A" (#5907). A filter that matches nothing still has
  // rows, so this is only ever the table with nothing in it at all.
  const hasRows = (baseOptions.data?.length ?? 0) > 0;
  const enableRowActions = Boolean(baseOptions.enableRowActions) && hasRows;
  const {
    displayColumnDefOptions,
    muiFilterTextFieldProps,
    muiSearchTextFieldProps,
  } = baseOptions;
  const shouldUseMuiV9RowSelectColumn = Boolean(baseOptions.enableRowSelection);
  const withRowSelectColumn = shouldUseMuiV9RowSelectColumn
    ? {
        ...displayColumnDefOptions,
        "mrt-row-select": {
          Cell: ({ row, staticRowIndex, table }: MrtSelectCellArgs<TData>) => (
            <TableSelectCheckbox row={row} staticRowIndex={staticRowIndex} table={table} />
          ),
          Header:
            baseOptions.enableSelectAll !== false && baseOptions.enableMultiRowSelection !== false
              ? ({ table }: { table: MRT_TableInstance<TData> }) => <TableSelectCheckbox table={table} />
              // Named for screen readers and invisible to everyone else, exactly as the actions
              // column is. Left to MRT this falls back to its own "Select" text, which in a 40px
              // column arrives clipped to "Selec" — a table that turns select-all off should show an
              // empty header there, not a broken word.
              : RowSelectColumnHeader,
          ...displayColumnDefOptions?.["mrt-row-select"],
        },
      }
    : displayColumnDefOptions;
  const tableControlsColumnVisibility =
    baseOptions.state?.columnVisibility !== undefined || baseOptions.onColumnVisibilityChange !== undefined;
  const [chosenColumnVisibility, setChosenColumnVisibility] = usePersistedColumnVisibility(
    tableControlsColumnVisibility ? undefined : props.persistColumnVisibilityAs,
  );
  const persistsColumnVisibility = Boolean(props.persistColumnVisibilityAs) && !tableControlsColumnVisibility;
  const tableOptions: MRT_TableOptions<TData> = {
    ...baseOptions,
    ...(persistsColumnVisibility
      ? {
          onColumnVisibilityChange: setChosenColumnVisibility,
          // Controlled, so MRT no longer reads `initialState.columnVisibility`: the table's own defaults
          // go underneath, and the reader's saved choice over them.
          state: {
            ...baseOptions.state,
            columnVisibility: { ...baseOptions.initialState?.columnVisibility, ...chosenColumnVisibility },
          },
        }
      : {}),
    enableRowActions,
    // Every table's actions column is presented the same way, so it is applied here rather than
    // restated in seventeen files: hidden header, centred controls, and no more width than the
    // controls need. The table's own options are spread after, so anything it sets still wins.
    displayColumnDefOptions: enableRowActions
      ? {
          ...withRowSelectColumn,
          "mrt-row-actions": {
            Header: RowActionsColumnHeader,
            minSize: oneCollapsedButton,
            size: oneCollapsedButton,
            ...withRowSelectColumn?.["mrt-row-actions"],
          },
        }
      : withRowSelectColumn,
    ...(enableRowActions
      ? {
          muiTableBodyCellProps: withRowActionsCellProps(baseOptions),
          muiTableHeadCellProps: withRowActionsHeadCellProps(baseOptions),
        }
      : {}),
    ...(isPhone ? withPhoneSizedPager(baseOptions) : {}),
    muiTableProps: withEmptyTableProps(baseOptions),
    muiTableContainerProps: args => {
      const inherited = withVisibleScrollbar(baseOptions)(args);
      return {
        ...inherited,
        onMouseOver: event => { inherited.onMouseOver?.(event); cellTooltip.onMouseOver(event); },
        onMouseOut: event => { inherited.onMouseOut?.(event); cellTooltip.onMouseOut(event); },
        onMouseLeave: event => { inherited.onMouseLeave?.(event); cellTooltip.clear(); },
        onScroll: event => { inherited.onScroll?.(event); cellTooltip.clear(); },
        onFocus: event => { inherited.onFocus?.(event); cellTooltip.onFocus(event); },
        onBlur: event => { inherited.onBlur?.(event); cellTooltip.clear(); },
      };
    },
    ...(props.scrollButtons ? { muiTablePaperProps: withPositionedPaper(baseOptions) } : {}),
    muiFilterTextFieldProps:
      typeof muiFilterTextFieldProps === "function"
        ? (filterProps) => withMrtTextFieldRootSlot(muiFilterTextFieldProps(filterProps))
        : withMrtTextFieldRootSlot(muiFilterTextFieldProps),
    muiSearchTextFieldProps:
      typeof muiSearchTextFieldProps === "function"
        ? (searchProps) => withMrtTextFieldRootSlot(muiSearchTextFieldProps(searchProps))
        : withMrtTextFieldRootSlot(muiSearchTextFieldProps),
  };
  const persistsOrder =
    Boolean(layoutKey) &&
    Boolean(tableOptions.enableColumnOrdering || tableOptions.enableColumnDragging) &&
    tableOptions.state?.columnOrder === undefined &&
    tableOptions.onColumnOrderChange === undefined;
  const baseTable = useMaterialReactTable<TData>(persistsOrder ? withReaderColumnOrder(tableOptions, layout) : tableOptions);
  useSaveColumnLayout(layoutKey, layout, Boolean(baseTable.getState().columnSizingInfo.isResizingColumn));
  const drawsCards = Boolean(props.cards) && isPhone && isListTable;
  // A card list has no columns to fit.
  useFitColumnsToPane(baseTable, fit, isListTable && !drawsCards);
  useTurnToRevealedRow(baseTable, props.revealRowId);
  useReportDrawnRows(baseTable, props.onDrawnRowsChange);

  const readerColumnChoice = persistsColumnVisibility ? chosenColumnVisibility : props.columnChoice;

  return (
    <ReaderColumnChoiceContext.Provider value={readerColumnChoice}>
      {drawsCards ? (
        // A card has room for every value in its own place, so nothing folds onto a title.
        <FoldedColumnsContext.Provider value={noFoldedColumns}>
          <TableCards
            table={baseTable}
            {...(typeof props.cards === "object" ? props.cards : {})}
          />
        </FoldedColumnsContext.Provider>
      ) : (
        <FoldedColumnsContext.Provider value={fit.foldedInto}>
          <MaterialReactTable table={baseTable} />
        </FoldedColumnsContext.Provider>
      )}
      {cellTooltip.tooltip}
      {/* Nothing to scroll to in a table with no rows: its empty state fits wherever its headers do not
          (#5907). */}
      {props.scrollButtons && hasRows && !drawsCards && <TableScrollButtons table={baseTable} />}
    </ReaderColumnChoiceContext.Provider>
  );
}
